/**
 * Parked vehicles: where cars stand when nobody is driving them.
 *
 * The map has no cars in it, but it says where they go:
 *
 * - **In front of a garage.** A garage knows which way its doors face (see
 *   `garages.ts`). Most have a car on the apron outside, nose to the door. A row of garages has one now and then along its front.
 * - **On a driveway** without a garage: beside the house, at the end away
 *   from the street.
 * - **In a car park.** A mapped `amenity=parking` is laid out as real car
 *   parks are: bays 2.5 m wide and 5 m deep, back to back in pairs of rows
 *   with a 6 m aisle between each pair, along the lot's long axis. How full
 *   it is changes from lot to lot.
 *
 * Every car is kept wholly inside its lot and clear of buildings. The kind and
 * the paint are drawn from what is actually on the road. White, black, grey and
 * silver are most of it, with a little colour. It is pure and seeded, so a
 * tile always parks the same cars in the same bays.
 *
 * Where a car park's aisles are mapped (`service=parking_aisle`), its bays
 * line them on both sides, square to the aisle, as the car park is really
 * laid out. Their markings are drawn from the same layout (`BayStrip`), so
 * every car stands in a marked bay.
 */

import type { Species } from '../biome/flora.js';
import { SPECIES_ID } from '../biome/flora.js';
import { orientedFootprintBox } from './footprintbox.js';
import type { TriangulatedPolygon } from './scatter.js';

export type VehicleSpecies = Extract<Species, 'carHatch' | 'carSedan' | 'carEstate' | 'carSuv' | 'carVan' | 'carPickup' | 'parkedMoto' | 'truckBox' | 'truckSemi' |
  'busArea' | 'busExpress' | 'busMetro' | 'busCity' | 'parkedScooter'>;

/** The two-wheelers: got on from the left, ridden in the motorbike's mode, knocked over by a shoulder. */
export const TWO_WHEELERS: ReadonlySet<string> = new Set(['parkedMoto', 'parkedScooter']);
export const isTwoWheeler = (species: string): boolean => TWO_WHEELERS.has(species);

/** Half length and half width of each kind, metres, for keeping them apart and for collision. */
export const VEHICLE_SIZE: Record<VehicleSpecies, { halfLength: number; halfWidth: number; height: number }> = {
  carHatch: { halfLength: 2.2, halfWidth: 0.92, height: 1.5 },
  carSedan: { halfLength: 2.38, halfWidth: 0.92, height: 1.47 },
  carEstate: { halfLength: 2.4, halfWidth: 0.92, height: 1.52 },
  carSuv: { halfLength: 2.33, halfWidth: 0.96, height: 1.8 },
  carVan: { halfLength: 2.52, halfWidth: 1.0, height: 1.96 },
  carPickup: { halfLength: 2.62, halfWidth: 0.98, height: 1.82 },
  parkedMoto: { halfLength: 1.05, halfWidth: 0.4, height: 1.2 },
  parkedScooter: { halfLength: 0.62, halfWidth: 0.31, height: 1.25 },
  // A semi's size is its tractor's; its trailer is a body of its own, behind (see `SEMI_REACH`).
  truckBox: { halfLength: 4.35, halfWidth: 1.28, height: 3.8 },
  truckSemi: { halfLength: 3.05, halfWidth: 1.28, height: 4.0 },
  // Buses: their origin is the middle of the body (`three/Bus.ts`).
  busArea: { halfLength: 6.0, halfWidth: 1.28, height: 3.3 },
  busExpress: { halfLength: 7.4, halfWidth: 1.28, height: 3.3 },
  busMetro: { halfLength: 6.0, halfWidth: 1.28, height: 3.3 },
  busCity: { halfLength: 6.0, halfWidth: 1.28, height: 3.3 },
};

/** How far an articulated truck reaches behind its tractor's middle, trailer and all, straight. */
export const SEMI_REACH = 13.7;

/** What is on the road, roughly: small cars and estates, SUVs, the odd van and pickup. */
const CAR_MIX: ReadonlyArray<[VehicleSpecies, number]> = [
  ['carHatch', 0.3], ['carSedan', 0.2], ['carEstate', 0.18], ['carSuv', 0.19], ['carVan', 0.08], ['carPickup', 0.05],
];

/**
 * Car paint as it is sold: white, black, grey and silver are three cars in
 * four. Colours are a touch brighter than the showroom, to sit in the diorama.
 */
const PAINTS: ReadonlyArray<[number, number, string]> = [
  [0xf2f2ee, 0.23, 'white'], [0x1d1f24, 0.18, 'black'], [0x7b8087, 0.15, 'grey'], [0xb8bcc2, 0.12, 'silver'],
  [0x2f5da8, 0.08, 'blue'], [0xc3312b, 0.08, 'red'], [0x2f5b43, 0.03, 'green'], [0x9c8566, 0.03, 'beige'],
  [0x1f3350, 0.04, 'navy'], [0xe6b93a, 0.02, 'yellow'], [0xdd7a2c, 0.02, 'orange'], [0x3d9c9a, 0.02, 'teal'],
];

/** The name of the paint nearest a colour, for "the red estate". */
export function paintName(hex: number): string {
  let best = 'grey', bestD = Infinity;
  for (const [paint, , name] of PAINTS) {
    let d = 0;
    for (const shift of [16, 8, 0]) d += (((paint >> shift) & 255) - ((hex >> shift) & 255)) ** 2;
    if (d < bestD) { bestD = d; best = name; }
  }
  return best;
}

/** What each kind is called. */
export const VEHICLE_NAME: Record<VehicleSpecies, string> = {
  carHatch: 'hatchback', carSedan: 'saloon', carEstate: 'estate', carSuv: 'SUV', carVan: 'van',
  carPickup: 'pickup', parkedMoto: 'motorbike', truckBox: 'box truck', truckSemi: 'articulated truck',
  busArea: 'bus', busExpress: 'express bus', busMetro: 'metro bus', busCity: 'bus', parkedScooter: 'KuKirin G2 e-scooter',
};

/**
 * Whether someone at (px, pz) is on the driver's side of a vehicle standing
 * at (cx, cz) facing `yaw`: out past its left flank, where the driver's door is
 * (the cars are left-hand drive, and a bike is got on from the left).
 * From there the walk to the door runs along the outside of the body,
 * never through it.
 */
export function onDriverSide(px: number, pz: number, cx: number, cz: number, yaw: number, species: VehicleSpecies): boolean {
  // Across the vehicle, positive to its right: local +X is (cos, -sin).
  const across = (px - cx) * Math.cos(yaw) - (pz - cz) * Math.sin(yaw);
  return across < -(VEHICLE_SIZE[species].halfWidth + 0.1);
}

export interface ParkedVehicle {
  x: number;
  z: number;
  /** Yaw in the sense of `Object3D.rotation.y`: the nose points along (-sin, -cos). */
  rotation: number;
  species: number;
  /** Body colour, 0xRRGGBB. */
  paint: number;
}

export interface GarageDoor {
  /** The middle of the door wall, on the outside. */
  x: number;
  z: number;
  /** Unit direction the doors face. */
  faceX: number;
  faceZ: number;
  /** Length of the door wall, metres: a row of garages is long. */
  span: number;
}

/**
 * A run of marked bays side by side in one row of a car park, for the
 * markings to be drawn from. Local u runs along the row from its first bay's
 * edge, v across it from the row's middle. The back line is at v = `back` × depth / 2.
 */
export interface BayStrip {
  /** Middle of the run. */
  x: number;
  z: number;
  /** Yaw of the u axis, in the sense of `Object3D.rotation.y`. */
  rotation: number;
  /** Bays in the run. */
  count: number;
  back: 1 | -1;
}

export interface ParkingInput {
  /** Each mapped car park, as its triangulated polygons. */
  lots: readonly TriangulatedPolygon[][];
  garages: readonly GarageDoor[];
  /** Driveways without a garage: from beside the house (x0, z0) to the street (x1, z1). */
  driveways: ReadonlyArray<{ x0: number; z0: number; x1: number; z1: number }>;
  /** Whether a point is inside a building, or otherwise no place for a car. */
  blocked: (x: number, z: number) => boolean;
  rng: () => number;
  /** Most cars a tile may park. */
  max?: number;
  /** Filled with every car park's marked bays, parked in or not. */
  bays?: BayStrip[];
  /**
   * Lanes a car can drive along: service roads and streets. The ones running
   * through a car park are its aisles, and its bays line them.
   */
  aisles?: ReadonlyArray<{ points: Float64Array; halfWidth: number }>;
}

/**
 * A bay, and the aisle between pairs of rows, metres. Roomy enough that the
 * longest pickup fits its bay and two cars reversing out of facing rows clear
 * each other.
 */
export const BAY_WIDTH = 2.6;
export const BAY_DEPTH = 5.6;
const AISLE = 7;
const MAX_PER_LOT = 80;

function pickWeighted<T>(table: ReadonlyArray<[T, number]>, u: number): T {
  let total = 0;
  for (const [, w] of table) total += w;
  let t = u * total;
  for (const [value, w] of table) {
    t -= w;
    if (t <= 0) return value;
  }
  return table[table.length - 1][0];
}

/** A kind of car as common as it is on the road. */
export function pickCar(u: number): VehicleSpecies {
  return pickWeighted(CAR_MIX, u);
}

/** Fleet colours for truck cabs: white most of all, then blues and reds, a green, a yellow, black. */
export const CAB_PAINTS = [0xf0f0ec, 0xf0f0ec, 0xf0f0ec, 0x23498c, 0x2f6fb5, 0xb3261e, 0xc8432b, 0x2e5d3b, 0xe0b028, 0x1f2124];

/** How far the trailer's box reaches behind a semi's middle, to its middle, and half its length: for collisions. */
export const SEMI_TRAILER_BOX = { behind: 7.05, halfLength: 6.85 };

export function pickPaint(u: number): number {
  return pickWeighted(PAINTS.map(([hex, w]) => [hex, w] as [number, number]), u);
}

/**
 * Lay a car park out along its mapped aisles: a row of bays down each side of
 * every aisle segment inside the lot, square to it, starting just clear of
 * the tarmac. Bays that would leave the lot, meet a building or road, or
 * overlap a bay already laid are left out. Returns false when the lot has no
 * aisles, for the caller to lay it out on its own.
 */
function parkAlongAisles(lot: readonly TriangulatedPolygon[], input: ParkingInput, open: Bay[]): boolean {
  const { rng, blocked } = input;
  const laid: Array<{ x: number; z: number; ux: number; uz: number }> = [];
  // Inside a bay already laid, with a hair's margin.
  const taken = (x: number, z: number) => laid.some((b) => {
    const dx = x - b.x, dz = z - b.z;
    return Math.abs(dx * b.ux + dz * b.uz) < BAY_WIDTH / 2 - 0.05 && Math.abs(-dx * b.uz + dz * b.ux) < BAY_DEPTH / 2 - 0.05;
  });
  let segments = 0;
  const full = 0.3 + rng() * 0.55;
  for (const aisle of input.aisles ?? []) {
    const p = aisle.points;
    for (let i = 0; i + 3 < p.length; i += 2) {
      const ax = p[i], az = p[i + 1], bx = p[i + 2], bz = p[i + 3];
      const length = Math.hypot(bx - ax, bz - az);
      if (length < BAY_WIDTH || !insideTriangles(lot, (ax + bx) / 2, (az + bz) / 2)) continue;
      segments++;
      const dx = (bx - ax) / length, dz = (bz - az) / length;
      const count = Math.floor(length / BAY_WIDTH);
      const start = (length - count * BAY_WIDTH) / 2;
      for (const side of [1, -1] as const) {
        // Out from the aisle: the normal on this side.
        const nx = -dz * side, nz = dx * side;
        const off = aisle.halfWidth + 0.45 + BAY_DEPTH / 2;
        const valid: boolean[] = [];
        const centres: Array<[number, number]> = [];
        for (let k = 0; k < count; k++) {
          const t = start + (k + 0.5) * BAY_WIDTH;
          const x = ax + dx * t + nx * off, z = az + dz * t + nz * off;
          centres.push([x, z]);
          const corners = [[-1, -1], [-1, 1], [1, -1], [1, 1], [0, 0]].map(([a, c]) =>
            [x + dx * a * (BAY_WIDTH / 2 - 0.05) + nx * c * (BAY_DEPTH / 2 - 0.05),
              z + dz * a * (BAY_WIDTH / 2 - 0.05) + nz * c * (BAY_DEPTH / 2 - 0.05)] as [number, number]);
          const ok = corners.every(([px, pz]) => insideTriangles(lot, px, pz) && !blocked(px, pz) && !taken(px, pz));
          valid.push(ok);
        }
        centres.forEach(([x, z], k) => { if (valid[k]) laid.push({ x, z, ux: dx, uz: dz }); });
        // The markings, a run at a time. Local u runs along the aisle; the
        // back of the bays is the end away from it.
        const rotation = Math.atan2(-dz, dx);
        const vx = Math.sin(rotation), vz = Math.cos(rotation);
        const back: 1 | -1 = vx * nx + vz * nz > 0 ? 1 : -1;
        for (let k = 0; k < count;) {
          if (!valid[k]) { k++; continue; }
          let e = k;
          while (e + 1 < count && valid[e + 1]) e++;
          const [x0, z0] = centres[k], [x1, z1] = centres[e];
          input.bays?.push({ x: (x0 + x1) / 2, z: (z0 + z1) / 2, rotation, count: e - k + 1, back });
          k = e + 1;
        }
        // Nose first means away from the aisle.
        centres.forEach(([x, z], k) => { if (valid[k]) open.push({ x, z, noseX: nx, noseZ: nz, full }); });
      }
    }
  }
  // Aisles that line no bays (a road along the edge, say) leave the lot to
  // the caller's own layout.
  return segments > 0 && laid.length > 0;
}

/** A marked bay a car may be parked in, and how full its car park is. */
interface Bay {
  x: number;
  z: number;
  /** Nose-in direction. */
  noseX: number;
  noseZ: number;
  full: number;
}

/** Yaw that points the nose along (fx, fz). */
function yawToward(fx: number, fz: number): number {
  return Math.atan2(-fx, -fz);
}

function insideTriangles(polys: readonly TriangulatedPolygon[], x: number, z: number): boolean {
  for (const poly of polys) {
    const v = poly.vertices, idx = poly.indices;
    for (let t = 0; t < idx.length; t += 3) {
      const ax = v[idx[t] * 2], az = v[idx[t] * 2 + 1];
      const bx = v[idx[t + 1] * 2], bz = v[idx[t + 1] * 2 + 1];
      const cx = v[idx[t + 2] * 2], cz = v[idx[t + 2] * 2 + 1];
      const d1 = (x - bx) * (az - bz) - (ax - bx) * (z - bz);
      const d2 = (x - cx) * (bz - cz) - (bx - cx) * (z - cz);
      const d3 = (x - ax) * (cz - az) - (cx - ax) * (z - az);
      const neg = d1 < 0 || d2 < 0 || d3 < 0;
      const pos = d1 > 0 || d2 > 0 || d3 > 0;
      if (!(neg && pos)) return true;
    }
  }
  return false;
}

/** The four corners of a vehicle's footprint, and its middle. */
function footprint(x: number, z: number, yaw: number, species: VehicleSpecies): Array<[number, number]> {
  const { halfLength: l, halfWidth: w } = VEHICLE_SIZE[species];
  const fx = -Math.sin(yaw), fz = -Math.cos(yaw);
  const rx = -fz, rz = fx;
  return [[x, z], ...[[1, 1], [1, -1], [-1, 1], [-1, -1]].map(([a, b]) =>
    [x + fx * l * a + rx * w * b, z + fz * l * a + rz * w * b] as [number, number])];
}

export function parkVehicles(input: ParkingInput): ParkedVehicle[] {
  const { rng, blocked } = input;
  const max = input.max ?? 320;
  const out: ParkedVehicle[] = [];
  const car = () => pickWeighted(CAR_MIX, rng());
  const clear = (x: number, z: number, yaw: number, species: VehicleSpecies) =>
    footprint(x, z, yaw, species).every(([px, pz]) => !blocked(px, pz)) &&
    out.every(o => Math.hypot(o.x - x, o.z - z) > 2.2);
  const add = (x: number, z: number, yaw: number, species: VehicleSpecies) => {
    out.push({ x, z, rotation: yaw, species: SPECIES_ID[species], paint: pickPaint(rng()) });
  };

  // Outside garages: nose to the door, a car's length out on the apron.
  for (const door of input.garages) {
    if (out.length >= max) break;
    const rx = -door.faceZ, rz = door.faceX;
    // A single garage has one door; a row has one every three metres.
    const doors = Math.max(1, Math.floor(door.span / 3));
    const chance = doors === 1 ? 0.7 : 0.25;
    for (let d = 0; d < doors; d++) {
      if (rng() >= chance) continue;
      const along = (d - (doors - 1) / 2) * (door.span / doors);
      // One in ten a motorbike; one in twelve an e-scooter, left by the door.
      const roll = rng();
      const species: VehicleSpecies = roll < 0.1 ? 'parkedMoto' : roll < 0.18 ? 'parkedScooter' : car();
      // Driven in, nose to the door, usually. Some reverse up to it.
      const yaw = rng() < 0.75 ? yawToward(-door.faceX, -door.faceZ) : yawToward(door.faceX, door.faceZ);
      // Just off the door, or a step further out if something is in the way.
      for (const gap of [0.7, 1.9]) {
        const out0 = VEHICLE_SIZE[species].halfLength + gap;
        const x = door.x + rx * along + door.faceX * out0, z = door.z + rz * along + door.faceZ * out0;
        if (!clear(x, z, yaw, species)) continue;
        add(x, z, yaw, species);
        break;
      }
    }
  }

  // Driveways: at the house end, nose to the house.
  for (const drive of input.driveways) {
    if (out.length >= max) break;
    const len = Math.hypot(drive.x1 - drive.x0, drive.z1 - drive.z0);
    if (len < 5.5 || rng() >= 0.4) continue;
    const ux = (drive.x1 - drive.x0) / len, uz = (drive.z1 - drive.z0) / len;
    const species = car();
    const at = VEHICLE_SIZE[species].halfLength + 0.4;
    const x = drive.x0 + ux * at, z = drive.z0 + uz * at;
    const yaw = yawToward(-ux, -uz);
    if (clear(x, z, yaw, species)) add(x, z, yaw, species);
  }

  // Trucks: in a big car park, now and then, one or two parked along its
  // long side near the edge, where trucks wait by a warehouse, a retail
  // park or a truck stop. They are what there is to drive for a living.
  const trucks: Array<{ x: number; z: number; fx: number; fz: number; front: number; back: number; halfWidth: number }> = [];
  for (const lot of input.lots) {
    let count = 0;
    for (const poly of lot) count += poly.vertices.length;
    const all = new Float64Array(count);
    let o = 0;
    for (const poly of lot) { all.set(poly.vertices, o); o += poly.vertices.length; }
    const box = orientedFootprintBox(all);
    if (box === undefined || box.width < 34 || box.depth < 12 || rng() >= 0.55) continue;
    const ux = Math.cos(box.rotation), uz = -Math.sin(box.rotation);
    const vx = -uz, vz = ux;
    const n = box.width >= 60 && rng() < 0.5 ? 2 : 1;
    for (let k = 0; k < n; k++) {
      const species: VehicleSpecies = rng() < 0.55 ? 'truckSemi' : 'truckBox';
      const size = VEHICLE_SIZE[species];
      const front = size.halfLength, back = species === 'truckSemi' ? SEMI_REACH : size.halfLength;
      // Facing along the lot, either way, by one of its long edges.
      const dir = rng() < 0.5 ? 1 : -1;
      const fx = ux * dir, fz = uz * dir;
      const side = rng() < 0.5 ? 1 : -1;
      const v = side * (box.depth / 2 - size.halfWidth - 1.2);
      const u = (k === 0 ? -1 : 1) * (n === 1 ? 0 : box.width / 4);
      // Its middle, so that from nose to tail it sits in the lot.
      const mid = u + dir * (back - front) / 2;
      const x = box.centreX + ux * mid + vx * v, z = box.centreZ + uz * mid + vz * v;
      // All of it in the lot and clear of buildings, every few metres along.
      let fits = true;
      for (let a = -front; a <= back && fits; a += 2) {
        for (const c of [-1, 0, 1]) {
          const px = x - fx * a + vx * c * size.halfWidth, pz = z - fz * a + vz * c * size.halfWidth;
          if (!insideTriangles(lot, px, pz) || blocked(px, pz)) { fits = false; break; }
        }
      }
      if (!fits) continue;
      out.push({ x, z, rotation: yawToward(fx, fz), species: SPECIES_ID[species], paint: CAB_PAINTS[Math.floor(rng() * CAB_PAINTS.length)] });
      trucks.push({ x, z, fx, fz, front, back, halfWidth: size.halfWidth });
    }
  }
  /** Whether a point is under one of the trucks: no car is parked there. */
  const underTruck = (px: number, pz: number) => trucks.some((t) => {
    const a = -((px - t.x) * t.fx + (pz - t.z) * t.fz), c = Math.abs(-(px - t.x) * t.fz + (pz - t.z) * t.fx);
    return a > -t.front - 1.5 && a < t.back + 1.5 && c < t.halfWidth + 1.5;
  });

  // Car parks: bays along both sides of each mapped aisle, the way the car
  // park is actually laid out. Where the map has no aisles, pairs of rows
  // back to back along the lot's long side, an aisle between each pair.
  // Every lot's bays are laid out and marked first; the cars are shared out
  // over all of them after, so a tile full of car parks fills each a little
  // rather than the first few to the brim and the rest not at all.
  const open: Bay[] = [];
  for (const lot of input.lots) {
    if (parkAlongAisles(lot, input, open)) continue;
    let count = 0;
    for (const poly of lot) count += poly.vertices.length;
    const all = new Float64Array(count);
    let o = 0;
    for (const poly of lot) { all.set(poly.vertices, o); o += poly.vertices.length; }
    const box = orientedFootprintBox(all);
    if (box === undefined || box.depth < BAY_DEPTH) continue;
    // Along the long side (u) and across it (v). Local +X is (cos, -sin).
    const ux = Math.cos(box.rotation), uz = -Math.sin(box.rotation);
    const vx = -uz, vz = ux;
    const full = 0.3 + rng() * 0.55;
    const bays = Math.floor(box.width / BAY_WIDTH);
    let parked = 0;
    const module = 2 * BAY_DEPTH + AISLE;
    // Centre the rows across the lot: as many pairs as fit, a lone row if one more does.
    const pairs = Math.floor((box.depth + AISLE) / module);
    const rows: Array<{ v: number; nose: 1 | -1 }> = [];
    for (let p = 0; p < pairs; p++) {
      const v0 = p * module;
      rows.push({ v: v0 + BAY_DEPTH / 2, nose: -1 }, { v: v0 + BAY_DEPTH * 1.5, nose: 1 });
    }
    const used = pairs * module - (pairs > 0 ? AISLE : 0);
    if (box.depth - used >= AISLE + BAY_DEPTH) rows.push({ v: used + AISLE + BAY_DEPTH / 2, nose: -1 });
    else if (rows.length === 0) rows.push({ v: BAY_DEPTH / 2, nose: 1 });
    const span = rows.length === 0 ? 0 : rows[rows.length - 1].v + BAY_DEPTH / 2;
    const vStart = -span / 2;
    const at = (u: number, v: number): [number, number] => [box.centreX + ux * u + vx * v, box.centreZ + uz * u + vz * v];
    for (const row of rows) {
      const v = vStart + row.v;
      // Which bays are really there: wholly in the lot, clear of buildings and roads.
      const valid: boolean[] = [];
      for (let b = 0; b < bays; b++) {
        const u = (b - (bays - 1) / 2) * BAY_WIDTH;
        const corners = [[-1, -1], [-1, 1], [1, -1], [1, 1], [0, 0]].map(([a, c]) =>
          at(u + a * (BAY_WIDTH / 2 - 0.05), v + c * (BAY_DEPTH / 2 - 0.05)));
        valid.push(corners.every(([px, pz]) => insideTriangles(lot, px, pz) && !blocked(px, pz)));
      }
      // Their markings, a run of neighbouring bays at a time.
      for (let b = 0; b < bays;) {
        if (!valid[b]) { b++; continue; }
        let e = b;
        while (e + 1 < bays && valid[e + 1]) e++;
        const [x, z] = at(((b + e) / 2 - (bays - 1) / 2) * BAY_WIDTH, v);
        // The back of the bays is the end away from the nose.
        input.bays?.push({ x, z, rotation: box.rotation, count: e - b + 1, back: row.nose === 1 ? -1 : 1 });
        b = e + 1;
      }
      for (let b = 0; b < bays; b++) {
        if (!valid[b] || parked >= MAX_PER_LOT) continue;
        const [x, z] = at((b - (bays - 1) / 2) * BAY_WIDTH, v);
        open.push({ x, z, noseX: vx * row.nose, noseZ: vz * row.nose, full });
        parked++;
      }
    }
  }

  // The cars, over every bay at once: each lot as full as it is, scaled down
  // together when the tile would go over its budget.
  let wanted = 0;
  for (const bay of open) wanted += bay.full;
  const share = Math.min(1, Math.max(0, max - out.length) / Math.max(1, wanted));
  for (const bay of open) {
    if (out.length >= max || rng() >= bay.full * share || underTruck(bay.x, bay.z)) continue;
    // Now and then a motorbike takes a bay, or an e-scooter stands in one.
    // Rarely: the bike is the most detailed model there is.
    const roll = rng();
    const species: VehicleSpecies = roll < 0.012 ? 'parkedMoto' : roll < 0.02 ? 'parkedScooter' : car();
    // Driven in nose first, mostly; one in five reversed in.
    const nose = rng() < 0.8 ? 1 : -1;
    const yaw = yawToward(bay.noseX * nose, bay.noseZ * nose);
    if (clear(bay.x, bay.z, yaw, species)) add(bay.x, bay.z, yaw, species);
  }
  return out;
}

/**
 * Exactly where a car may not stand: inside a building's outline, on a road
 * with traffic, in water.
 *
 * The props' occupancy grid is too coarse for this. Its 5 m cells, rounded
 * outward, cover the apron in front of every garage door, which is exactly
 * where a car belongs. So this tests the real shapes, found through a
 * spatial hash.
 */
export class Obstacles {
  private static readonly CELL = 16;
  private readonly cells = new Map<number, number[]>();
  private readonly rings: Float64Array[] = [];
  private readonly segments: number[] = [];
  private readonly triangles: number[] = [];

  constructor(
    buildings: readonly Float64Array[],
    roads: ReadonlyArray<{ points: Float64Array; halfWidth: number }>,
    water: readonly TriangulatedPolygon[] = [],
  ) {
    for (const ring of buildings) {
      let x0 = Infinity, z0 = Infinity, x1 = -Infinity, z1 = -Infinity;
      for (let i = 0; i < ring.length; i += 2) {
        x0 = Math.min(x0, ring[i]); x1 = Math.max(x1, ring[i]);
        z0 = Math.min(z0, ring[i + 1]); z1 = Math.max(z1, ring[i + 1]);
      }
      this.index(x0, z0, x1, z1, 0, this.rings.length);
      this.rings.push(ring);
    }
    for (const road of roads) {
      const p = road.points;
      for (let i = 0; i + 3 < p.length; i += 2) {
        const hw = road.halfWidth + 0.3;
        const id = this.segments.length / 5;
        this.segments.push(p[i], p[i + 1], p[i + 2], p[i + 3], hw);
        this.index(Math.min(p[i], p[i + 2]) - hw, Math.min(p[i + 1], p[i + 3]) - hw,
          Math.max(p[i], p[i + 2]) + hw, Math.max(p[i + 1], p[i + 3]) + hw, 1, id);
      }
    }
    for (const poly of water) {
      const v = poly.vertices;
      for (let t = 0; t + 2 < poly.indices.length; t += 3) {
        const a = poly.indices[t] * 2, b = poly.indices[t + 1] * 2, c = poly.indices[t + 2] * 2;
        const id = this.triangles.length / 6;
        this.triangles.push(v[a], v[a + 1], v[b], v[b + 1], v[c], v[c + 1]);
        this.index(Math.min(v[a], v[b], v[c]), Math.min(v[a + 1], v[b + 1], v[c + 1]),
          Math.max(v[a], v[b], v[c]), Math.max(v[a + 1], v[b + 1], v[c + 1]), 2, id);
      }
    }
  }

  /** File an obstacle of `kind` (0 building, 1 road segment, 2 water triangle) under every cell its box touches. */
  private index(x0: number, z0: number, x1: number, z1: number, kind: number, id: number): void {
    const c = Obstacles.CELL;
    for (let gx = Math.floor(x0 / c); gx <= Math.floor(x1 / c); gx++) {
      for (let gz = Math.floor(z0 / c); gz <= Math.floor(z1 / c); gz++) {
        const key = gx * 100003 + gz;
        let list = this.cells.get(key);
        if (list === undefined) this.cells.set(key, (list = []));
        list.push(kind, id);
      }
    }
  }

  blocked(x: number, z: number): boolean {
    const c = Obstacles.CELL;
    const list = this.cells.get(Math.floor(x / c) * 100003 + Math.floor(z / c));
    if (list === undefined) return false;
    for (let i = 0; i < list.length; i += 2) {
      const kind = list[i], id = list[i + 1];
      if (kind === 0 && inRing(this.rings[id], x, z)) return true;
      if (kind === 1) {
        const s = this.segments, k = id * 5;
        if (segmentDistance(s[k], s[k + 1], s[k + 2], s[k + 3], x, z) < s[k + 4]) return true;
      }
      if (kind === 2) {
        const t = this.triangles, k = id * 6;
        if (inTriangle(x, z, t[k], t[k + 1], t[k + 2], t[k + 3], t[k + 4], t[k + 5])) return true;
      }
    }
    return false;
  }
}

function inRing(ring: Float64Array, x: number, z: number): boolean {
  let inside = false;
  const n = ring.length / 2;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = ring[i * 2], zi = ring[i * 2 + 1], xj = ring[j * 2], zj = ring[j * 2 + 1];
    if ((zi > z) !== (zj > z) && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi) inside = !inside;
  }
  return inside;
}

function segmentDistance(ax: number, az: number, bx: number, bz: number, x: number, z: number): number {
  const dx = bx - ax, dz = bz - az;
  const len2 = dx * dx + dz * dz;
  const t = len2 > 0 ? Math.max(0, Math.min(1, ((x - ax) * dx + (z - az) * dz) / len2)) : 0;
  return Math.hypot(x - (ax + dx * t), z - (az + dz * t));
}

function inTriangle(x: number, z: number, ax: number, az: number, bx: number, bz: number, cx: number, cz: number): boolean {
  const d1 = (x - bx) * (az - bz) - (ax - bx) * (z - bz);
  const d2 = (x - cx) * (bz - cz) - (bx - cx) * (z - cz);
  const d3 = (x - ax) * (cz - az) - (cx - ax) * (z - az);
  return !((d1 < 0 || d2 < 0 || d3 < 0) && (d1 > 0 || d2 > 0 || d3 > 0));
}
